import { useState } from 'react';
import useScrollAnimation from '../hooks/useScrollAnimation';
import Lightbox from '../components/Lightbox';

// ── 25th Anniversary ──
import a01 from '../imagesForCategory/25th Anniversary/A 01.jpg';
import a02 from '../imagesForCategory/25th Anniversary/A 02.jpg';
import a03 from '../imagesForCategory/25th Anniversary/A 03.jpg';
import a04 from '../imagesForCategory/25th Anniversary/A 04.jpg';
import a05 from '../imagesForCategory/25th Anniversary/A 05.jpg';
import a06 from '../imagesForCategory/25th Anniversary/A 06.jpg';

const allImages = [a01, a02, a03, a04, a05, a06];

export default function Anniversary() {
  useScrollAnimation();
  const [lightboxIdx, setLightboxIdx] = useState(null);

  return (
    <main>
      {/* ═══ INTRO ═══ */}
      <section className="py-20 md:py-28 bg-charcoal">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center fade-up">
          <p className="text-gold text-[10px] tracking-[0.3em] uppercase font-montserrat mb-2">
            25 Years of Love
          </p>
          <h2 className="font-playfair text-4xl md:text-5xl text-cream mb-4">
            Silver Jubilee Celebrations
          </h2>
          <div className="gold-line mb-6" />
          <p className="text-cream/55 text-sm md:text-base leading-[1.8] font-light max-w-3xl mx-auto">
            The 25th wedding anniversary is a milestone that celebrates an enduring bond built on love, trust, and shared experiences.
            At Sabitha Studio, we honor this beautiful journey by capturing the joy of reuniting with family, the warmth of
            renewed vows, and the elegance of silver jubilee celebrations with our signature cinematic photography and videography.
          </p>
        </div>
      </section>

      {/* ═══ GALLERY ═══ */}
      <section className="py-20 md:py-28 bg-charcoal-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12 md:mb-16 text-center fade-up">
            <p className="text-gold text-[10px] tracking-[0.3em] uppercase font-montserrat mb-2">
              Captured Moments
            </p>
            <h2 className="font-playfair text-4xl md:text-5xl text-cream mb-4">
              Gallery
            </h2>
            <div className="gold-line mb-6" />
          </div>

          {/* Gallery grid */}
          <div className="space-y-4">
            {/* Top row — hero + side */}
            <div className="grid md:grid-cols-12 gap-4">
              <div
                className="md:col-span-8 album-card scale-up aspect-[4/3] md:aspect-auto md:min-h-[500px] cursor-pointer"
                onClick={() => setLightboxIdx(0)}
              >
                <img src={allImages[0]} alt="Anniversary featured" loading="lazy" />
                <div className="overlay">
                  <div>
                    <span className="text-cream text-xs font-montserrat tracking-[0.1em] uppercase block mb-1">Featured</span>
                    <span className="text-cream/50 text-[10px] font-montserrat">1 of {allImages.length}</span>
                  </div>
                </div>
              </div>
              <div className="md:col-span-4 grid gap-4">
                {allImages.slice(1, 3).map((img, i) => (
                  <div
                    key={i}
                    className="album-card scale-up aspect-[4/3] cursor-pointer"
                    onClick={() => setLightboxIdx(i + 1)}
                  >
                    <img src={img} alt={`Anniversary ${i + 2}`} loading="lazy" />
                    <div className="overlay">
                      <span className="text-cream/70 text-[10px] font-montserrat">{i + 2}/{allImages.length}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Bottom row */}
            <div className="grid grid-cols-3 gap-4 stagger">
              {allImages.slice(3).map((img, i) => (
                <div
                  key={i}
                  className="album-card scale-up aspect-[4/3] cursor-pointer"
                  onClick={() => setLightboxIdx(i + 3)}
                >
                  <img src={img} alt={`Anniversary ${i + 4}`} loading="lazy" />
                  <div className="overlay">
                    <span className="text-cream/70 text-[10px] font-montserrat">{i + 4}/{allImages.length}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ WHY CELEBRATE WITH US ═══ */}
      <section className="py-20 md:py-28 bg-charcoal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
            <div className="fade-left">
              <div className="grid grid-cols-2 gap-4">
                <div className="album-card aspect-[3/4]">
                  <img src={a03} alt="Anniversary couple" loading="lazy" />
                </div>
                <div className="album-card aspect-[3/4] mt-8">
                  <img src={a05} alt="Anniversary celebration" loading="lazy" />
                </div>
              </div>
            </div>
            <div className="fade-right">
              <p className="text-gold text-[10px] tracking-[0.3em] uppercase font-montserrat mb-2">
                Why Choose Sabitha Studio
              </p>
              <h2 className="font-playfair text-3xl md:text-4xl text-cream mb-6">
                Celebrate Love That <span className="italic text-gold">Lasts Forever</span>
              </h2>
              <div className="space-y-4 text-cream/55 text-sm leading-[1.8] font-light">
                <p>
                  Your 25th anniversary deserves a celebration as special as the love you share. With over 35 years of experience
                  capturing life's most cherished moments, Sabitha Studio brings unmatched expertise, creativity, and heart to
                  every anniversary shoot.
                </p>
                <p>
                  From intimate couple portraits to grand family gatherings, we document the warmth, joy, and togetherness
                  that define your silver jubilee celebration.
                </p>
                <ul className="space-y-2 text-cream/50">
                  <li className="flex items-start gap-2">
                    <span className="text-gold mt-1">&#10022;</span>
                    Cinematic photography & videography
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold mt-1">&#10022;</span>
                    Couple portraits with artistic lighting
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold mt-1">&#10022;</span>
                    Family group photos & candid moments
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold mt-1">&#10022;</span>
                    Custom photo albums & digital delivery
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold mt-1">&#10022;</span>
                    Indoor & outdoor location shoots
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ PARALLAX QUOTE ═══ */}
      <section
        className="parallax-section py-24 md:py-32"
        style={{ backgroundImage: `url(${a04})` }}
      >
        <div className="max-w-3xl mx-auto px-4 text-center fade-up">
          <svg className="w-10 h-10 text-gold/40 mx-auto mb-6" fill="currentColor" viewBox="0 0 24 24">
            <path d="M4.583 17.321C3.553 16.227 3 15 3 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179zm10 0C13.553 16.227 13 15 13 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179z" />
          </svg>
          <p className="font-cormorant text-2xl md:text-3xl lg:text-4xl text-cream italic leading-relaxed mb-6">
            Twenty-five years of walking together, laughing together, and growing together — this is the love story worth celebrating.
          </p>
          <div className="gold-line mb-4" />
          <p className="text-gold/60 text-xs tracking-[0.2em] uppercase font-montserrat">
            Sabitha Studio
          </p>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section className="py-20 md:py-28 bg-charcoal">
        <div className="max-w-4xl mx-auto px-4 text-center fade-up">
          <p className="text-gold text-xs tracking-[0.3em] uppercase font-montserrat mb-3">Celebrate Your Milestone</p>
          <h2 className="font-playfair text-4xl md:text-5xl text-cream mb-6">
            Book Your <span className="italic text-gold">Anniversary Shoot</span>
          </h2>
          <div className="gold-line mb-8" />
          <p className="text-cream/60 text-base md:text-lg mb-12 max-w-2xl mx-auto font-light">
            Let us help you celebrate 25 years of love with stunning photography that captures the joy,
            warmth, and togetherness of your silver jubilee.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+919703377033" className="btn-gold">Call +91-9703377033</a>
            <a href="https://wa.me/919703377033" target="_blank" rel="noopener noreferrer" className="btn-outline">WhatsApp Us</a>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxIdx !== null && (
        <Lightbox
          image={allImages[lightboxIdx]}
          onClose={() => setLightboxIdx(null)}
          onPrev={lightboxIdx > 0 ? () => setLightboxIdx((i) => i - 1) : null}
          onNext={lightboxIdx < allImages.length - 1 ? () => setLightboxIdx((i) => i + 1) : null}
        />
      )}
    </main>
  );
}
